export const POLICY_NAMES = {
  general: 'กฎความปลอดภัยทั่วไปและบทลงโทษด้านความปลอดภัย',
  employee: 'นโยบายความเป็นส่วนตัวสำหรับพนักงาน',
  candidate: 'นโยบายความเป็นส่วนตัวสำหรับผู้สมัครงาน',
};

export const POLICY_UI = {
  general: {
    key: 'general',
    prefKey: 'generalPolicy',
    icon: 'fa-gavel',
    title: 'นโยบายทั่วไป (General Policy)',
    description: 'กฎระเบียบเรื่องการคุ้มครองข้อมูลและความปลอดภัยภายในองค์กร',
    required: true,
  },
  employee: {
    key: 'employee',
    prefKey: 'employeePolicy',
    icon: 'fa-file-lines',
    title: 'นโยบายความเป็นส่วนตัวพนักงาน',
    description: 'รายละเอียดการเก็บรวบรวมและใช้ข้อมูลส่วนบุคคลของพนักงานตาม PDPA',
    required: true,
  },
  candidate: {
    key: 'candidate',
    prefKey: 'candidatePolicy',
    icon: 'fa-file-lines',
    title: 'นโยบายความเป็นส่วนตัวสำหรับผู้สมัครงาน',
    description: 'รายละเอียดการเก็บรวบรวมและใช้ข้อมูลส่วนบุคคลของผู้สมัครงานตาม PDPA',
    required: true,
  },
};

export const COOKIE_OPTIONS = [
  {
    key: 'necessary',
    prefKey: null,
    icon: 'fa-shield-halved',
    title: 'คุกกี้ที่จำเป็น (Strictly Necessary)',
    description: 'จำเป็นอย่างยิ่งสำหรับการทำงานพื้นฐานของเว็บไซต์',
    required: true,
  },
  {
    key: 'functional',
    prefKey: 'functional',
    icon: 'fa-language',
    title: 'คุกกี้เพื่อการทำงาน (Functional)',
    description: 'จดจำการตั้งค่า เช่น ภาษาและภูมิภาค',
    required: false,
  },
  {
    key: 'performance',
    prefKey: 'performance',
    icon: 'fa-chart-line',
    title: 'คุกกี้ประสิทธิภาพ (Performance)',
    description: 'วิเคราะห์การใช้งานเพื่อปรับปรุงประสบการณ์',
    required: false,
  },
  {
    key: 'marketing',
    prefKey: 'marketing',
    icon: 'fa-bullhorn',
    title: 'คุกกี้การตลาด (Marketing)',
    description: 'แสดงเนื้อหาและโฆษณาที่เกี่ยวข้อง',
    required: false,
  },
  {
    key: 'hr',
    prefKey: 'hr',
    icon: 'fa-users',
    title: 'ระบบงานบุคคล (HR)',
    description: 'รองรับฟีเจอร์ที่เกี่ยวข้องกับข้อมูลพนักงาน',
    required: false,
    employeeOnly: true,
  },
];

export function createDefaultPreferences(userType = 'employee') {
  return {
    generalPolicy: true,
    employeePolicy: userType === 'employee',
    candidatePolicy: userType === 'applicant',
    functional: false,
    performance: false,
    marketing: false,
    hr: false,
  };
}

export function createAllowAllPreferences(userType = 'employee') {
  return {
    generalPolicy: true,
    employeePolicy: userType === 'employee',
    candidatePolicy: userType === 'applicant',
    functional: true,
    performance: true,
    marketing: true,
    hr: userType === 'employee',
  };
}

export function resolvePdpaIdentity(user) {
  if (!user) return null;
  if (user.candidateId) {
    return { empCode: `CND_${user.candidateId}`, userType: 'applicant' };
  }
  if (user.empCode) {
    return { empCode: user.empCode, userType: 'employee' };
  }
  return null;
}

export function normalizeVersionId(item) {
  if (item == null) return null;
  if (typeof item === 'number' && Number.isFinite(item)) return item;
  if (typeof item === 'string') {
    const parsed = Number.parseInt(item, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof item === 'object') {
    const raw = item.VersionID
      ?? item.versionID
      ?? item.versionId
      ?? item.versionid
      ?? item.policyVersionId
      ?? item.PolicyVersionID
      ?? item.policyVersionID
      ?? item.version?.VersionID
      ?? item.version?.versionID
      ?? item.policy?.VersionID;
    if (raw == null) return null;
    const parsed = Number.parseInt(String(raw), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function isAcceptedConsentRecord(item) {
  if (!item || typeof item !== 'object') return false;
  const status = String(
    item.status ?? item.Status ?? item.consentStatus ?? item.ConsentStatus ?? 'ACCEPTED',
  ).toUpperCase();
  return status !== 'REVOKED' && status !== 'REJECTED' && status !== 'DECLINED';
}

/** Consent VersionIDs accepted in *this* system only (matched by VersionID). */
export function collectAcceptedVersionIds(acceptedPolicies) {
  const acceptedIds = new Set();
  const records = Array.isArray(acceptedPolicies) ? acceptedPolicies : [];

  for (const item of records) {
    if (!isAcceptedConsentRecord(item)) continue;
    const id = normalizeVersionId(item);
    if (id != null) acceptedIds.add(id);
  }

  return acceptedIds;
}

export function findPolicyByName(policies, name) {
  if (!Array.isArray(policies)) return null;
  return policies.find((policy) => String(policy.PolicyName || '').trim() === name) || null;
}

export function normalizeActivePolicies(policies) {
  if (!Array.isArray(policies)) return [];
  return policies.filter((policy) => {
    const versionId = normalizeVersionId(policy);
    if (versionId == null) return false;
    if (policy.IsActive === false) return false;
    const status = String(policy.Status || 'ACTIVE').toUpperCase();
    return status !== 'INACTIVE';
  });
}

export function getPolicyIcon(policyName = '') {
  const name = String(policyName);
  if (name.includes('คุกกี้') || /cookie/i.test(name)) return 'fa-cookie-bite';
  if (name.includes('ความปลอดภัย') || name.includes('กฎ')) return 'fa-gavel';
  if (name.includes('ค้ำประกัน') || name.includes('ยินยอม')) return 'fa-file-signature';
  return 'fa-file-lines';
}

export function toPolicyDisplayRow(policy) {
  return {
    versionId: normalizeVersionId(policy),
    icon: getPolicyIcon(policy.PolicyName),
    title: policy.PolicyName,
    description: policy.EffectiveDate
      ? `มีผลบังคับใช้ ${new Date(policy.EffectiveDate).toLocaleDateString('th-TH')}`
      : 'นโยบายที่ต้องยอมรับก่อนใช้งานระบบ',
  };
}

/** @deprecated use normalizeActivePolicies — kept for legacy name-map lookups */
export function mapActivePolicies(policies) {
  const list = normalizeActivePolicies(policies);
  return {
    general: findPolicyByName(list, POLICY_NAMES.general),
    employee: findPolicyByName(list, POLICY_NAMES.employee),
    candidate: findPolicyByName(list, POLICY_NAMES.candidate),
  };
}

export function getRequiredVersionIds(userType, policyList) {
  const list = normalizeActivePolicies(policyList);
  if (userType === 'applicant') {
    const candidate = findPolicyByName(list, POLICY_NAMES.candidate);
    return candidate?.VersionID != null ? [candidate.VersionID] : [];
  }
  return list
    .map((policy) => normalizeVersionId(policy))
    .filter((id) => id != null);
}

export function getPolicyDisplayRows(policyList) {
  return normalizeActivePolicies(policyList).map(toPolicyDisplayRow);
}

export function getPolicyDisplayRowsForUser(userType, policyList) {
  const list = normalizeActivePolicies(policyList);
  if (userType === 'applicant') {
    const candidate = findPolicyByName(list, POLICY_NAMES.candidate);
    return candidate ? [toPolicyDisplayRow(candidate)] : [];
  }
  return list.map(toPolicyDisplayRow);
}

export function isFullyConsented(requiredVersionIds, acceptedPolicies) {
  const requiredIds = (requiredVersionIds || [])
    .map((id) => normalizeVersionId(id))
    .filter((id) => id != null);
  if (!requiredIds.length) return false;

  const acceptedIds = collectAcceptedVersionIds(acceptedPolicies);
  return requiredIds.every((id) => acceptedIds.has(id));
}

export function mergePreferencesFromConsents(acceptedPolicies, userType) {
  const prefs = createDefaultPreferences(userType);
  if (!Array.isArray(acceptedPolicies)) return prefs;

  for (const item of acceptedPolicies) {
    const source = item?.preferences;
    if (!source || typeof source !== 'object') continue;
    Object.keys(prefs).forEach((key) => {
      if (typeof source[key] === 'boolean') prefs[key] = source[key];
    });
  }
  return prefs;
}

function unwrapPdpaPayload(result) {
  if (!result || typeof result !== 'object') return result;
  if (result.resCode) return result;
  if (result.data?.resCode) return result.data;
  if (result.data && (Array.isArray(result.data) || result.data.acceptedPolicies || Array.isArray(result.data.result))) {
    return result.data;
  }
  return result;
}

function extractPolicyList(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.Data)) return payload.Data;
  return [];
}

function extractAcceptedPolicies(payload) {
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.acceptedPolicies)) return payload.acceptedPolicies;
  if (Array.isArray(payload.consents)) return payload.consents;
  if (Array.isArray(payload.result)) return payload.result;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && typeof payload.data === 'object') {
    return extractAcceptedPolicies(payload.data);
  }
  return [];
}

function assertPdpaSuccess(payload) {
  if (!payload) throw new Error('PDPA response is empty');
  if (payload.resCode && payload.resCode !== '0000') {
    throw new Error(payload.resMessage || payload.resDesc || 'PDPA request failed');
  }
  return payload;
}

async function pdpaRequest(fetchFn, path, options = {}) {
  const result = await fetchFn(path, options);
  return assertPdpaSuccess(unwrapPdpaPayload(result));
}

export async function fetchActivePolicies(fetchFn) {
  const payload = await pdpaRequest(fetchFn, '/pdpa/policy/active');
  return normalizeActivePolicies(extractPolicyList(payload));
}

export async function fetchConsents(fetchFn, empCode, userType = 'employee') {
  const payload = await pdpaRequest(fetchFn, '/pdpa/client/get-consents', {
    method: 'POST',
    body: { empCode, userType },
  });
  return extractAcceptedPolicies(payload);
}

function clientMeta() {
  return {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

export async function submitConsent(fetchFn, { empCode, versionID, userType, preferences }) {
  return pdpaRequest(fetchFn, '/pdpa/client/submit', {
    method: 'POST',
    body: {
      empCode,
      versionID,
      status: 'ACCEPTED',
      userType,
      preferences,
      ...clientMeta(),
    },
  });
}

export async function updatePreferences(fetchFn, { empCode, versionID, userType, preferences }) {
  return pdpaRequest(fetchFn, '/pdpa/client/update-preferences', {
    method: 'POST',
    body: {
      empCode,
      versionID,
      userType,
      preferences,
      ...clientMeta(),
    },
  });
}

export async function revokeConsent(fetchFn, { empCode, versionID, userType, reason }) {
  return pdpaRequest(fetchFn, '/pdpa/client/revoke', {
    method: 'POST',
    body: {
      empCode,
      versionID,
      userType,
      reason: reason || 'User requested via settings',
      ...clientMeta(),
    },
  });
}

export async function saveConsentsForVersions(fetchFn, {
  empCode,
  userType,
  versionIds,
  preferences,
  acceptedVersionIds = [],
}) {
  const accepted = new Set(
    (Array.isArray(acceptedVersionIds) ? acceptedVersionIds : [...acceptedVersionIds])
      .map((id) => normalizeVersionId(id))
      .filter((id) => id != null),
  );

  await Promise.all(
    versionIds.map((versionID) => {
      const id = normalizeVersionId(versionID);
      const action = accepted.has(id) ? updatePreferences : submitConsent;
      return action(fetchFn, { empCode, versionID: id, userType, preferences });
    }),
  );
}

export async function revokeAllConsents(fetchFn, { empCode, userType, versionIds, reason }) {
  await Promise.all(
    versionIds.map((versionID) => revokeConsent(fetchFn, {
      empCode,
      versionID,
      userType,
      reason,
    })),
  );
}
